import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Search, Trash2, X, GitMerge } from "lucide-react";
import { PageHead } from "@/components/Filters";
import { api, errMsg } from "@/lib/api";

const KINDS = [
  { key: "colleges", label: "Colleges", hasState: true },
  { key: "courses", label: "Courses" },
  { key: "categories", label: "Categories", hasQuota: true },
  { key: "quotas", label: "Quotas" },
];

export default function AdminMasters() {
  const [kind, setKind] = useState("colleges");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState({ name: "", state: "", quota: "" });
  const [aliasDrafts, setAliasDrafts] = useState({});
  const [mergeFrom, setMergeFrom] = useState(null);

  const cfg = useMemo(() => KINDS.find((k) => k.key === kind), [kind]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/masters/${kind}`, { params: q ? { q } : {} });
      setItems(data.items || []);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setAdding(false);
    setNewRow({ name: "", state: "", quota: "" });
    setMergeFrom(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const add = async () => {
    try {
      await api.post(`/admin/masters/${kind}`, newRow);
      toast.success(`Added to ${cfg.label}`);
      setAdding(false);
      setNewRow({ name: "", state: "", quota: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const addAlias = async (id) => {
    const alias = (aliasDrafts[id] || "").trim();
    if (!alias) return;
    try {
      await api.post(`/admin/masters/${kind}/${id}/aliases`, { alias });
      setAliasDrafts((p) => ({ ...p, [id]: "" }));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const removeAlias = async (id, alias) => {
    try {
      await api.delete(`/admin/masters/${kind}/${id}/aliases`, { params: { alias } });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const deleteItem = async (id, name) => {
    if (!window.confirm(`Delete '${name}'? This is blocked if any live records still reference it.`)) return;
    try {
      await api.delete(`/admin/masters/${kind}/${id}`);
      toast.success(`'${name}' deleted`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const merge = async (intoId, intoName) => {
    if (!mergeFrom) return;
    if (!window.confirm(`Merge '${mergeFrom.name}' into '${intoName}'? All aliases + live records will be rewritten.`)) return;
    try {
      await api.post(`/admin/masters/${kind}/merge`, { from_id: mergeFrom.id, into_id: intoId });
      toast.success(`Merged '${mergeFrom.name}' into '${intoName}'`);
      setMergeFrom(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-masters-page">
      <PageHead
        title="Canonical masters"
        subtitle="Four always-on libraries. Every upload resolves its raw strings against these before publish."
      />

      <div className="flex gap-1 overflow-x-auto">
        {KINDS.map((k) => (
          <button
            key={k.key}
            data-testid={`masters-tab-${k.key}`}
            onClick={() => setKind(k.key)}
            className={`whitespace-nowrap border px-4 py-2 text-xs font-semibold uppercase tracking-widest ${
              kind === k.key
                ? "border-[#1A365D] bg-[#1A365D] text-white"
                : "border-slate-300 bg-white text-slate-600 hover:border-[#1A365D]"
            }`}
          >
            {k.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 border border-slate-200 bg-white p-3">
        <Search className="h-4 w-4 text-slate-400" />
        <input
          data-testid="masters-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder={`Search ${cfg.label} by name or alias…`}
          className="h-9 flex-1 border border-slate-300 px-3 text-sm"
        />
        <button
          data-testid="masters-reload-btn"
          onClick={load}
          className="h-9 border border-slate-300 bg-white px-3 text-xs font-semibold uppercase tracking-widest text-slate-700 hover:border-[#1A365D]"
        >
          Reload
        </button>
        <button
          data-testid="masters-add-new-btn"
          onClick={() => setAdding((p) => !p)}
          className="flex h-9 items-center gap-1 bg-[#1A365D] px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#122845]"
        >
          <Plus className="h-3 w-3" /> Add new
        </button>
      </div>

      {adding && (
        <div className="grid gap-3 border border-slate-200 bg-white p-4 sm:grid-cols-4" data-testid="masters-new-form">
          <input
            data-testid="masters-new-name"
            value={newRow.name}
            onChange={(e) => setNewRow((p) => ({ ...p, name: e.target.value }))}
            placeholder="Canonical name"
            className="h-9 border border-slate-300 px-2 text-sm sm:col-span-2"
          />
          {cfg.hasState && (
            <input
              data-testid="masters-new-state"
              value={newRow.state}
              onChange={(e) => setNewRow((p) => ({ ...p, state: e.target.value }))}
              placeholder="State"
              className="h-9 border border-slate-300 px-2 text-sm"
            />
          )}
          {cfg.hasQuota && (
            <input
              data-testid="masters-new-quota"
              value={newRow.quota}
              onChange={(e) => setNewRow((p) => ({ ...p, quota: e.target.value }))}
              placeholder="Linked quota"
              className="h-9 border border-slate-300 px-2 text-sm"
            />
          )}
          <button
            data-testid="masters-new-save"
            onClick={add}
            disabled={!newRow.name.trim()}
            className="flex h-9 items-center justify-center gap-1 bg-emerald-600 px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-emerald-700 disabled:opacity-40"
          >
            <Save className="h-3 w-3" /> Save
          </button>
        </div>
      )}

      {mergeFrom && (
        <div
          className="border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="merge-picker"
        >
          <strong>Merge mode:</strong> pick any other row to absorb <strong>{mergeFrom.name}</strong> into it.
          <button
            onClick={() => setMergeFrom(null)}
            className="ml-2 text-xs font-semibold uppercase text-amber-700 hover:text-amber-900"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="border border-slate-200 bg-white">
        {loading ? (
          <p className="p-6 text-center text-sm text-slate-400">Loading…</p>
        ) : items.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">No {cfg.label.toLowerCase()} yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="masters-list">
            {items.map((it) => (
              <li key={it.id} className="px-4 py-3" data-testid={`masters-row-${it.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <strong className="text-sm text-slate-900">{it.name}</strong>
                  {it.state && (
                    <span className="border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-500">
                      {it.state}
                    </span>
                  )}
                  {it.quota && (
                    <span className="border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-800">
                      quota: {it.quota}
                    </span>
                  )}
                  {it.counselling_body && (
                    <span className="border border-indigo-300 bg-indigo-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-indigo-800">
                      {it.counselling_body}
                    </span>
                  )}
                  <span className="font-mono-num text-xs text-slate-400">
                    {it.aliases.length} alias{it.aliases.length === 1 ? "" : "es"}
                  </span>
                  <div className="ml-auto flex items-center gap-2">
                    {mergeFrom ? (
                      mergeFrom.id === it.id ? (
                        <span className="text-[11px] font-semibold text-amber-700">source</span>
                      ) : (
                        <button
                          data-testid={`merge-into-${it.id}`}
                          onClick={() => merge(it.id, it.name)}
                          className="text-[11px] font-semibold text-amber-700 hover:underline"
                        >
                          Merge into this
                        </button>
                      )
                    ) : (
                      <button
                        data-testid={`merge-start-${it.id}`}
                        onClick={() => setMergeFrom(it)}
                        title="Merge this into another"
                        className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-[#1A365D]"
                      >
                        <GitMerge className="h-3 w-3" /> Merge
                      </button>
                    )}
                    <button
                      data-testid={`masters-delete-${it.id}`}
                      onClick={() => deleteItem(it.id, it.name)}
                      className="text-slate-400 hover:text-rose-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {it.aliases.map((a) => (
                    <span
                      key={a}
                      className="flex items-center gap-1 border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-xs text-slate-700"
                    >
                      {a}
                      <button
                        data-testid={`masters-alias-remove-${it.id}-${a}`}
                        onClick={() => removeAlias(it.id, a)}
                        className="text-slate-400 hover:text-rose-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    data-testid={`masters-alias-input-${it.id}`}
                    value={aliasDrafts[it.id] || ""}
                    onChange={(e) =>
                      setAliasDrafts((p) => ({ ...p, [it.id]: e.target.value }))
                    }
                    onKeyDown={(e) => e.key === "Enter" && addAlias(it.id)}
                    placeholder="Add alias…"
                    className="h-7 border border-slate-200 px-2 text-xs"
                  />
                  <button
                    data-testid={`masters-alias-add-${it.id}`}
                    onClick={() => addAlias(it.id)}
                    className="h-7 border border-slate-300 bg-white px-2 text-[11px] font-semibold text-slate-700 hover:border-[#1A365D]"
                  >
                    Add
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
